import React from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import MunicipalitySelect from "../../components/Delivery/MunicipalitySelect";
import {
  DeliveryConfig,
  DeliveryMethod,
  DeliveryZone,
  DELIVERY_HOME,
  DELIVERY_PICKUP,
} from "../../utils/deliveryQuote";
import { formatCOP } from "../../utils/format";
import { DELIVERY_TEXTS } from "../shared/constants";

type Props = {
  zones: DeliveryZone[] | undefined;
  config: DeliveryConfig | undefined;
  method: DeliveryMethod;
  municipalityId: string;
  address: string;
  instructions: string;
  municipalityError: string | null;
  addressError: string | null;
  /** The rejection `POST /orders` came back with, shown here because this is what it is about. */
  serverError: string | null;
  onMethodChange: (method: DeliveryMethod) => void;
  onMunicipalityChange: (id: string) => void;
  onAddressChange: (address: string) => void;
  onInstructionsChange: (instructions: string) => void;
};

/**
 * Section 2 of checkout: how the order gets to the shopper.
 *
 * The municipality is pre-filled from whatever they chose on a product page, which is the whole
 * point of persisting it — arriving here with the fee already known and already agreed to is the
 * difference between a confirmation and a surprise.
 */
const DeliverySection: React.FC<Props> = ({
  zones,
  config,
  method,
  municipalityId,
  address,
  instructions,
  municipalityError,
  addressError,
  serverError,
  onMethodChange,
  onMunicipalityChange,
  onAddressChange,
  onInstructionsChange,
}) => {
  const isPickup = method === DELIVERY_PICKUP;
  const selectedZone = zones?.find((zone) => zone.id === municipalityId) ?? null;
  const threshold = selectedZone?.free_shipping_threshold_cop;
  // `0` is "always free" and `null` is "no threshold here" — a truthiness check would hide the
  // first and is the bug this whole field keeps inviting.
  const showThreshold = threshold !== null && threshold !== undefined;

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {DELIVERY_TEXTS.sectionTitle}
      </Typography>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {serverError}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary">
        {DELIVERY_TEXTS.methodLabel}
      </Typography>
      <RadioGroup
        value={method}
        onChange={(event) => onMethodChange(event.target.value as DeliveryMethod)}
      >
        <FormControlLabel
          value={DELIVERY_HOME}
          control={<Radio />}
          label={DELIVERY_TEXTS.methodDelivery}
        />
        {/* Hidden entirely when the shop has pickup switched off, rather than offered and then
            rejected by the server. */}
        {config?.pickup_enabled !== false && (
          <FormControlLabel
            value={DELIVERY_PICKUP}
            control={<Radio />}
            label={DELIVERY_TEXTS.methodPickup}
          />
        )}
      </RadioGroup>

      {isPickup ? (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {DELIVERY_TEXTS.pickupAt(config?.pickup_address ?? "")}
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <MunicipalitySelect
            zones={zones}
            value={municipalityId}
            onChange={onMunicipalityChange}
            fullWidth
            error={Boolean(municipalityError)}
            helperText={
              municipalityError ??
              (showThreshold ? DELIVERY_TEXTS.freeShippingFrom(formatCOP(Number(threshold))) : "")
            }
          />
          <TextField
            label={DELIVERY_TEXTS.addressLabel}
            placeholder={DELIVERY_TEXTS.addressPlaceholder}
            value={address}
            onChange={(event) => onAddressChange(event.target.value)}
            error={Boolean(addressError)}
            helperText={addressError ?? ""}
            size="small"
            fullWidth
          />
          <TextField
            label={DELIVERY_TEXTS.instructionsLabel}
            placeholder={DELIVERY_TEXTS.instructionsPlaceholder}
            value={instructions}
            onChange={(event) => onInstructionsChange(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </Box>
      )}
    </Box>
  );
};

export default DeliverySection;
